import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, ClipboardList, Package, Users, FileBarChart, UserCog, LogOut, Menu, Download, Wallet, PackageSearch, Settings2, CalendarDays } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL } from "../lib/constants";

export const NAV = [
  { to: "/", label: "Beranda", icon: LayoutDashboard, roles: ["admin", "head_operator", "operator", "gudang"], key: "dashboard" },
  { to: "/order", label: "Order", icon: ClipboardList, roles: ["admin", "head_operator", "operator"], key: "orders" },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, roles: ["admin", "head_operator", "operator"], key: "calendar" },
  { to: "/stok", label: "Stok", icon: Package, roles: ["admin", "gudang", "head_operator"], key: "stock" },
  { to: "/stok/laporan", label: "Laporan Stok", icon: PackageSearch, roles: ["admin", "gudang"], key: "stock-report" },
  { to: "/laporan", label: "Laporan", icon: FileBarChart, roles: ["admin"], key: "reports" },
  { to: "/keuangan", label: "Keuangan", icon: Wallet, roles: ["admin"], key: "finance" },
  { to: "/pelanggan", label: "Pelanggan", icon: Users, roles: ["admin"], key: "customers" },
  { to: "/pengguna", label: "Pengguna", icon: UserCog, roles: ["admin"], key: "users" },
  { to: "/pengaturan", label: "Pengaturan", icon: Settings2, roles: ["admin"], key: "settings" },
];

function InstallButton() {
  const [evt, setEvt] = useState(null);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  if (!evt) return null;
  return (
    <button data-testid="pwa-install-btn" onClick={() => { evt.prompt(); setEvt(null); }}
      className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl bg-sky-50 text-sky-700 text-sm font-bold border border-sky-200">
      <Download className="w-4 h-4" /> Pasang
    </button>
  );
}

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 h-12 rounded-xl font-semibold transition-colors ${isActive ? "bg-sky-500 text-white" : "text-slate-600 hover:bg-slate-100"}`;

function SideNav({ items, onClick }) {
  return (
    <nav className="space-y-1">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} onClick={onClick} data-testid={`nav-${n.key}`}>
          <n.icon className="w-5 h-5" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function BottomNav({ items, onMenu }) {
  const shown = items.length > 5 ? items.slice(0, 4) : items;
  const cls = ({ isActive }) => `flex-1 flex flex-col items-center gap-1 py-2 text-[11px] font-bold ${isActive ? "text-sky-600" : "text-slate-500"}`;
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 flex px-1 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_20px_rgba(15,23,42,0.06)]">
      {shown.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} className={cls} data-testid={`bottom-nav-${n.key}`}>
          <n.icon className="w-6 h-6" /> {n.label}
        </NavLink>
      ))}
      {items.length > 5 && (
        <button onClick={onMenu} className="flex-1 flex flex-col items-center gap-1 py-2 text-[11px] font-bold text-slate-500" data-testid="bottom-nav-menu">
          <Menu className="w-6 h-6" /> Menu
        </button>
      )}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user.role));
  const UserBox = (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-slate-900 text-white grid place-items-center font-extrabold">{user.name[0]?.toUpperCase()}</div>
      <div className="min-w-0">
        <div className="font-bold text-sm truncate" data-testid="current-user-name">{user.name}</div>
        <div className="text-xs text-slate-500" data-testid="current-user-role">{ROLE_LABEL[user.role]}</div>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-slate-50 lg:pl-72">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 flex-col bg-white border-r border-slate-200 p-5 gap-6">
        <Brand />
        <SideNav items={items} />
        <div className="mt-auto space-y-4">{UserBox}
          <button onClick={logout} data-testid="logout-btn-desktop" className="w-full flex items-center gap-2 px-4 h-12 rounded-xl text-rose-600 font-semibold hover:bg-rose-50"><LogOut className="w-5 h-5" /> Keluar</button>
        </div>
      </aside>
      <header className="lg:hidden sticky top-0 z-30 backdrop-blur-md bg-white/90 border-b border-slate-200 px-4 h-16 flex items-center justify-between">
        <Brand compact />
        <div className="flex items-center gap-2">
          <InstallButton />
          <button onClick={logout} data-testid="logout-btn" className="w-10 h-10 grid place-items-center rounded-xl border border-slate-200 text-rose-600" aria-label="Keluar"><LogOut className="w-5 h-5" /></button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-5 lg:py-8 pb-28 lg:pb-10 space-y-6">
        <Outlet />
      </main>
      <BottomNav items={items} onMenu={() => setOpen(true)} />
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl">
          <SheetHeader><SheetTitle>Menu</SheetTitle></SheetHeader>
          <div className="py-4 space-y-4">{UserBox}<SideNav items={items} onClick={() => setOpen(false)} /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function Brand({ compact }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="brand">
      <div className="w-10 h-10 rounded-xl bg-sky-500 grid place-items-center text-white font-black text-lg shadow-md shadow-sky-500/30">M</div>
      <div className="leading-tight">
        <div className="font-extrabold tracking-tight text-slate-900">MACCA</div>
        <div className={`text-[10px] font-bold tracking-[0.2em] text-slate-500 ${compact ? "" : ""}`}>DESIGN PRINTING</div>
      </div>
    </div>
  );
}
